import { THIN_THRESHOLD } from './boxTruckZones'

/**
 * Определение зоны по точке касания.
 *
 * Чистая функция без DOM: её поведение — самая хрупкая часть схемы
 * (палец в перчатке, мороз, машина под погрузкой), и проверять его
 * надо тестами, а не руками на устройстве.
 *
 * ── Два правила и почему именно они ───────────────────────────────
 *
 * 1. Точное попадание важнее близости. Если палец внутри зоны,
 *    выигрывает САМАЯ МЕЛКАЯ из накрывших его зон: стекло лежит
 *    внутри двери, запор — поверх ворот, и человек, целившийся
 *    в стекло, имел в виду стекло, а не дверь.
 *
 * 2. Промах рядом с тонкой зоной — это попадание. Верхняя кромка
 *    будки и запоры ворот в реальном масштабе тоньше пальца.
 *    Поэтому у зон тоньше порога зона нажатия расширяется до
 *    минимума, и при промахе мимо всего побеждает ближайшая
 *    из расширенных.
 *
 * Порядок именно такой: если бы инфляция участвовала в первом
 * правиле, расширенная кромка «съедала» бы центр борта — тап
 * по середине панели попадал бы в кромку, потому что у кромки
 * площадь меньше.
 */

const area = (r) => r.w * r.h

const inside = (r, x, y) =>
  x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h

/** Расширение тонкой зоны до минимальной зоны нажатия. */
function inflate(rect, minHit) {
  const dw = Math.max(0, minHit - rect.w) / 2
  const dh = Math.max(0, minHit - rect.h) / 2
  return { x: rect.x - dw, y: rect.y - dh, w: rect.w + dw * 2, h: rect.h + dh * 2 }
}

/** Расстояние от точки до прямоугольника (0, если внутри). */
function distanceTo(r, x, y) {
  const dx = Math.max(r.x - x, 0, x - (r.x + r.w))
  const dy = Math.max(r.y - y, 0, y - (r.y + r.h))
  return Math.hypot(dx, dy)
}

/**
 * @param {Array} zones — зоны проекции
 * @param {number} x, y — точка в единицах viewBox
 * @param {number} minHit — минимальная зона нажатия в тех же единицах
 * @returns {object|null} зона или null, если мимо силуэта
 */
export function hitTestZone(zones, x, y, minHit = THIN_THRESHOLD) {
  if (!zones?.length) return null

  // Правило 1: точные попадания, побеждает наименьшая площадь.
  let exact = null
  for (const zone of zones) {
    if (!inside(zone.rect, x, y)) continue
    if (!exact || area(zone.rect) < area(exact.rect)) exact = zone
  }
  if (exact) return exact

  // Правило 2: промах — добираем только тонкие зоны, ближайшую.
  let nearest = null
  let nearestDist = Infinity
  for (const zone of zones) {
    const { w, h } = zone.rect
    if (w >= minHit && h >= minHit) continue // не тонкая — не расширяем
    if (!inside(inflate(zone.rect, minHit), x, y)) continue
    const d = distanceTo(zone.rect, x, y)
    if (d < nearestDist) {
      nearest = zone
      nearestDist = d
    }
  }
  return nearest
}

/** Точка внутри зоны → нормализованные 0..1 координаты проекции. */
export function normalize(x, y, viewBox) {
  return {
    x: Math.min(1, Math.max(0, x / viewBox.width)),
    y: Math.min(1, Math.max(0, y / viewBox.height)),
  }
}

/** Обратное преобразование — для отрисовки сохранённых точек. */
export function denormalize(nx, ny, viewBox) {
  return { x: nx * viewBox.width, y: ny * viewBox.height }
}
